import { Swiper } from 'swiper';
import { A11y, Autoplay, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { Fancybox } from '@fancyapps/ui';

if (!customElements.get('product-media-slider')) {
  class ProductMediaSlider extends HTMLElement {
    constructor() {
      super();
      // Get navigation buttons
      this.navigation = this.querySelectorAll('[data-swiper-nav-button]');

      // Get product data
      this.productData = JSON.parse(this.dataset.product) || {};

      // State for the current slide
      this.currentSlide = 0;
      this.currentSlideLightbox = 0;

      // Create elements
      this.mainSlider();
      this.fancybox();

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        slideToVariant: (e) => this.slideToVariant(e),
      };

      // Listen for variant change and go to slide
      document.addEventListener('variant-change', this.reducer.slideToVariant);
    }

    connectedCallback() {
      // Define variables for timer and timeout duration
      let timer;
      let timeOut = parseInt(this.dataset.resetTime) || 3000;

      // Function to hide navigation
      const hideNavigation = () => {
        document.body.classList.add('swiper-engaged');
      };

      // Function to reset timer and show navigation
      const resetTimer = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          document.body.classList.remove('swiper-engaged');
        }, timeOut);
      };

      // Event listener for slide change transition start
      this.swiperMainInstance.on('slideChangeTransitionStart', () => {
        // Hide navigation
        hideNavigation();
        // Reset timer to show navigation after timeout
        resetTimer();
      });
    }

    /*
     * Create main slider
     */
    mainSlider() {
      // Check if Swiper exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      // Get swiper element
      this.swiperMain = this.querySelector('[data-product-media-slider]');

      // Get initial slide and swiper index
      let initialSlideIndex = 0;
      const initalVariant = parseInt(this.swiperMain.dataset.productMediaSlider);

      if (initalVariant) {
        const initialSlide = this.matchVariantIdWithSlide(initalVariant);
        initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
      }

      // Swiper options
      this.swiperMainOptions = {
        modules: [A11y, Autoplay, Navigation, Pagination, Scrollbar],
        threshold: 10,
        loop: true,
        direction: 'horizontal',
        initialSlide: initialSlideIndex,
        Pagination: false,
        Scrollbar: false,
        navigation: {
          prevEl: this.querySelector('.product-media-slider__button-prev'),
          nextEl: this.querySelector('.product-media-slider__button-next'),
        },
      };

      // Create Main slider
      this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

      // Add event listener for slide change to send data to Elevar
      this.swiperMainInstance.on('slideChange', (swiper) => {
        if (this.currentSlide === swiper.activeIndex) {
          return;
        }
        this.currentSlide = swiper.activeIndex;
        if (window.ElevarDataLayer) {
          window.ElevarDataLayer.push({
            event: 'slide_change_pdp',
            slide: this.currentSlide,
            product: this.productData,
          });
        }
      });

      // Rebuid Swiper in design mode
      if (Shopify.designMode && this.swiperMainInstance) {
        window.addEventListener('shopify:section:load', () => {
          this.swiperMainInstance.update();
        });
      }
    }

    /*
     * Create fancybox
     */
    fancybox() {
      // Check if Fancybox exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      Fancybox.bind('[data-fancybox]', {
        animated: false,
        showClass: false,
        mainClass: 'image-zoom',
        // Listen for slide change to send data to Elevar
        on: {
          init: (fancybox, slide) => {
            window.ElevarDataLayer.push({
              event: 'open_lightbox',
              slide: this.currentSlideLightbox,
              product: this.productData,
            });
          },
          'Carousel.settle': (fancybox, slide) => {
            if (this.currentSlideLightbox === slide.pageIndex) {
              return;
            }
            this.currentSlideLightbox = slide.pageIndex;
            if (window.ElevarDataLayer) {
              window.ElevarDataLayer.push({
                event: 'slide_change_lightbox',
                slide: this.currentSlideLightbox,
                product: this.productData,
              });
            }
          },
        },
      });
    }

    /*
     * Slide to variant
     * @Param e {object}: event object with variant
     */
    slideToVariant(e) {
      if (e?.detail?.variant?.id) {
        const slideIndex = this.matchVariantIdWithSlide(e.detail.variant.id);
        if (typeof slideIndex == 'number') {
          this.goToSlide(slideIndex);
        }
      }
    }

    /*
     * Match variant id with slide
     * @param id {int}: variant id
     */
    matchVariantIdWithSlide(id) {
      if (!id || !this.swiperMain) {
        return false;
      }

      // Get slides
      const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

      // Match id with slides id's
      const slide = [...slides].find((slide) => {
        // Get variant id's attached to slide
        const slideVariants = JSON.parse(slide.dataset.productVariantImage);

        // Check if variant id match the slide variant id's
        if (slideVariants.includes(id)) {
          return slide;
        }
      });

      // return false if no slide found
      if (!slide) {
        return false;
      }

      // return matched slide index
      return getNodeIndex(slide);
    }

    /*
     * go to slide
     * @param slide {node} / {int}: slide node / index
     */
    goToSlide(slide) {
      if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) {
        return false;
      }

      // Get index, transform node to slide index or string to int
      const index = typeof slide == 'number' ? parseInt(slide) : getNodeIndex(slide);

      // go to slide
      this.swiperMainInstance.slideTo(index);
    }
  }

  window.ProductMediaSlider = ProductMediaSlider;

  customElements.define('product-media-slider', ProductMediaSlider);
}
